// Catalogue API — CRUD for brands and models.
// Reads + creates are delegated to the import feature to avoid duplication.
// This file adds the rename (PATCH) and delete (DELETE) endpoints.

import { api } from "@/shared/api/client";
import type {
  BrandUpdateResult,
  ModelUpdateResult,
  LoanCatalogueType,
  CatalogueColor,
  ColorUpdateResult,
} from "./types";
import {
  mockUpdateBrand,
  mockDeleteBrand,
  mockUpdateModel,
  mockDeleteModel,
  mockGetColors,
  mockCreateColor,
  mockUpdateColor,
  mockDeleteColor,
} from "./mock";

// Same toggle convention as import/api.ts — flip VITE_USE_MOCK=false to go live.
const USE_MOCK =
  (import.meta.env.VITE_USE_MOCK ?? "true").toString().toLowerCase() !== "false";

const delay = (ms = 300) => new Promise<void>((r) => setTimeout(r, ms));

// Re-export the read + create functions so catalogue components import from one place.
export {
  getBrands,
  getBrandModels,
  createBrand,
  createModel,
} from "../import/api";

// ── Rename brand ─────────────────────────────────────────────────────────────

/** PATCH /support/agreements/brands/{id}/?type= {name} → {id, name} */
export async function updateBrand(
  type: LoanCatalogueType,
  id: number,
  name: string,
): Promise<BrandUpdateResult> {
  if (USE_MOCK) {
    await delay();
    return mockUpdateBrand(type, id, name);
  }
  const res = await api.patch<BrandUpdateResult>(
    `/support/agreements/brands/${id}/`,
    { name },
    { params: { type } },
  );
  return res.data;
}

// ── Delete brand (cascade-hides its models) ───────────────────────────────────

/** DELETE /support/agreements/brands/{id}/?type= → 204 */
export async function deleteBrand(
  type: LoanCatalogueType,
  id: number,
): Promise<void> {
  if (USE_MOCK) {
    await delay();
    return mockDeleteBrand(type, id);
  }
  await api.delete(`/support/agreements/brands/${id}/`, { params: { type } });
}

// ── Rename model ─────────────────────────────────────────────────────────────

/** PATCH /support/agreements/brand-models/{id}/?type= {name} → {id, name, canonical} */
export async function updateModel(
  type: LoanCatalogueType,
  id: number,
  name: string,
): Promise<ModelUpdateResult> {
  if (USE_MOCK) {
    await delay();
    return mockUpdateModel(type, id, name);
  }
  const res = await api.patch<ModelUpdateResult>(
    `/support/agreements/brand-models/${id}/`,
    { name },
    { params: { type } },
  );
  return res.data;
}

// ── Delete model ──────────────────────────────────────────────────────────────

/** DELETE /support/agreements/brand-models/{id}/?type= → 204 */
export async function deleteModel(
  type: LoanCatalogueType,
  id: number,
): Promise<void> {
  if (USE_MOCK) {
    await delay();
    return mockDeleteModel(type, id);
  }
  await api.delete(`/support/agreements/brand-models/${id}/`, { params: { type } });
}

// ── Color CRUD (global — no type scope) ───────────────────────────────────────

/** GET /support/agreements/colors/ → CatalogueColor[] */
export async function getColors(): Promise<CatalogueColor[]> {
  if (USE_MOCK) {
    await delay();
    return mockGetColors();
  }
  const res = await api.get<CatalogueColor[]>("/support/agreements/colors/");
  return res.data;
}

/** POST /support/agreements/colors/ {name, name_ru?} → {id, name, name_ru, created} */
export async function createColor(
  name: string,
  name_ru?: string,
): Promise<{ id: number; name: string; name_ru?: string | null; created: boolean }> {
  if (USE_MOCK) {
    await delay();
    return mockCreateColor(name);
  }
  const res = await api.post<{ id: number; name: string; name_ru?: string | null; created: boolean }>(
    "/support/agreements/colors/",
    { name, name_ru },
  );
  return res.data;
}

/** PATCH /support/agreements/colors/{id}/ {name, name_ru?} → ColorUpdateResult */
export async function updateColor(
  id: number,
  name: string,
  name_ru?: string,
): Promise<ColorUpdateResult> {
  if (USE_MOCK) {
    await delay();
    return mockUpdateColor(id, name);
  }
  const res = await api.patch<ColorUpdateResult>(
    `/support/agreements/colors/${id}/`,
    { name, name_ru },
  );
  return res.data;
}

/** DELETE /support/agreements/colors/{id}/ → 204 */
export async function deleteColor(id: number): Promise<void> {
  if (USE_MOCK) {
    await delay();
    return mockDeleteColor(id);
  }
  await api.delete(`/support/agreements/colors/${id}/`);
}
